import { regularCoverages } from 'data-layer-cr/dist/functions/coverages';
import {
  coverageForCode,
  getRegularCoveragesForUnit,
} from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { moneyFormat } from 'design-system';
import { useMemo } from 'react';
import {
  BODILY_INJURIES,
  DAMAGE_TO_THIRD_PARTIES,
  LUC,
} from 'src/constants/coveragesCodes';
import {
  getDeductibleDefaultBase,
  getDeductibleDisplayText,
  getDeductibleHelperText,
} from 'src/functions/coverageMethods';
import {
  getTypeOfQuotation,
  typeOfQuotation,
} from 'src/functions/individualOptionHelpers';
import {
  CoverageCustomizable,
  makeEmptyCoverageCustomizable,
} from 'src/models/Quotation/CoverageCuztomization';

const { formatMoney } = moneyFormat;

export type UseCoverageDataProps = {
  unit: QuotationUnit;
  coverageCatalogs: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  moneyType: string | undefined;
  LUCOption?: boolean;
  packageOption?: number;
  createdAt?: string;
};

export type CoverageDetail = {
  code: string;
  name: string;
  value: string;
};

export type DeductibleDetail = {
  code: string;
  name: string;
  value: string;
  helperText: string;
  showHelper: boolean;
};

export const getFilterCoverage = (hasLuc: boolean): string[] => {
  return hasLuc ? [BODILY_INJURIES, DAMAGE_TO_THIRD_PARTIES] : [];
};

export function useCoverageData({
  unit,
  coverageCatalogResumes,
  coverageCatalogs,
  moneyType,
  LUCOption = false,
  packageOption = 1,
  createdAt,
}: UseCoverageDataProps): {
  customizableCoverages: CoverageCustomizable[];
  coveragesResume: CoverageDetail[];
  deductibleResume: DeductibleDetail[];
} {
  const usedCoverages = useMemo(
    () => getRegularCoveragesForUnit(unit, coverageCatalogResumes),
    [unit, coverageCatalogResumes],
  );
  const customizableCoverages = useMemo((): CoverageCustomizable[] => {
    const filterCoverages = getFilterCoverage(LUCOption);
    const limitedCoverages = coverageCatalogs.filter(
      (coverage) =>
        coverage.limits.length > 0 && !filterCoverages.includes(coverage.code),
    );
    return limitedCoverages.map((coverage): CoverageCustomizable => {
      const coverageData = coverageForCode(usedCoverages, coverage.code);
      const customizable = makeEmptyCoverageCustomizable();
      coverage.limits.forEach((limit, index) => {
        const catalogItem = {
          Value: `${index + 1}`,
          Text: formatMoney(limit.maximum, false, moneyType),
        };
        customizable.options.push(catalogItem);
        if (limit.maximum === coverageData?.insuredValue) {
          customizable.defaultValue = catalogItem;
        }
      });
      coverage.limits.forEach((limit, index) => {
        customizable.limits.push({
          id: `${index + 1}`,
          value: limit.maximum,
        });
      });
      customizable.name = coverage.name;
      customizable.code = coverage.code;

      return customizable;
    });
  }, [LUCOption, coverageCatalogs, moneyType, usedCoverages]);

  const coveragesResume = useMemo((): CoverageDetail[] => {
    const resume: CoverageDetail[] = [];
    regularCoverages(coverageCatalogs).forEach(({ code, name }) => {
      const coverageData = coverageForCode(usedCoverages, code);
      if (coverageData) {
        const newCovDetail: CoverageDetail = {
          code,
          name,
          value: formatMoney(coverageData.insuredValue, false, moneyType),
        };
        if (code === LUC) {
          resume.unshift(newCovDetail);
        } else {
          resume.push(newCovDetail);
        }
      }
    });
    return resume;
  }, [coverageCatalogs, usedCoverages, moneyType]);

  const deductibleResume = useMemo(() => {
    const resume: DeductibleDetail[] = [];
    regularCoverages(coverageCatalogs).forEach(({ code, name }) => {
      const coverageData = coverageForCode(usedCoverages, code);
      if (coverageData) {
        const deductibleType = getDeductibleDefaultBase(
          packageOption,
          code,
          createdAt || '',
        );
        const quotationType = getTypeOfQuotation(createdAt);
        let deductibleRate = coverageData.deductible;
        const { packageOptions } = typeOfQuotation[quotationType];
        if (packageOptions?.[packageOption] && coverageData.deductibleRate) {
          deductibleRate = coverageData.deductibleRate;
        }

        const displayText = getDeductibleDisplayText(
          deductibleType,
          deductibleRate,
          coverageData.deductibleRate ?? 0,
          moneyType,
          code,
          packageOption,
          createdAt || '',
        );

        const helperText = getDeductibleHelperText(
          deductibleType,
          coverageData.deductible,
          moneyType,
        );

        const showHelper =
          deductibleType === 'P' ? true : Boolean(deductibleRate);
        const newDeductibleDetail: DeductibleDetail = {
          code,
          name,
          value: displayText,
          helperText,
          showHelper,
        };
        if (code === LUC) {
          resume.unshift(newDeductibleDetail);
        } else {
          resume.push(newDeductibleDetail);
        }
      }
    });
    return resume;
  }, [coverageCatalogs, moneyType, packageOption, usedCoverages, createdAt]);

  return { customizableCoverages, coveragesResume, deductibleResume };
}
